import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

const STORAGE_KEY = 'neia_cookie_consent';

export interface CookieConsent {
  necessary: true; // sempre true — cookies de sessão/JWT não são opcionais
  analytics: boolean;
  marketing: boolean;
  decidedAt: string | null;
}

interface CookieConsentContextType {
  consent: CookieConsent;
  hasDecided: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  savePreferences: (partial: { analytics: boolean; marketing: boolean }) => void;
}

const DEFAULT_CONSENT: CookieConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  decidedAt: null
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

function readStoredConsent(): CookieConsent {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONSENT;
    const parsed = JSON.parse(raw);
    return {
      necessary: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      decidedAt: parsed.decidedAt || null
    };
  } catch (_err) {
    return DEFAULT_CONSENT;
  }
}

function writeStoredConsent(consent: CookieConsent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch (_err) {
    // localStorage indisponível (aba anônima com bloqueio, etc.) — segue sem persistir
  }
}

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent>(DEFAULT_CONSENT);

  useEffect(() => {
    setConsent(readStoredConsent());
  }, []);

  const persist = (next: CookieConsent) => {
    setConsent(next);
    writeStoredConsent(next);
  };

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true, decidedAt: new Date().toISOString() });
  const rejectNonEssential = () => persist({ necessary: true, analytics: false, marketing: false, decidedAt: new Date().toISOString() });
  const savePreferences = (partial: { analytics: boolean; marketing: boolean }) =>
    persist({ necessary: true, analytics: partial.analytics, marketing: partial.marketing, decidedAt: new Date().toISOString() });

  return (
    <CookieConsentContext.Provider
      value={{ consent, hasDecided: !!consent.decidedAt, acceptAll, rejectNonEssential, savePreferences }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export function useCookieConsent(): CookieConsentContextType {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent deve ser usado dentro de CookieConsentProvider');
  return ctx;
}
